import { useCallback, useRef, useState } from 'react';
import type {
    ConversationDetail,
    ConversationSummary,
} from '@ctd/agent-contract';
import { useStore } from '~contexts/mobx';
import {
    AgentUnauthorizedError,
    fetchConversationDetail,
    fetchConversationPage,
} from '../utils/conversations-api';

interface LoadedPages {
    /** The refresh these pages belong to. */
    generation: number;
    conversations: ConversationSummary[];
    page: number;
    perPage: number;
    total: number;
}

export interface ConversationHistory {
    /** Every page loaded so far, most recent activity first. */
    conversations: ConversationSummary[];
    total: number;
    hasMore: boolean;
    isLoadingMore: boolean;
    hasError: boolean;
    /** Reloads from the first page. */
    refresh: () => void;
    /** Appends the next page; once a page failed, waits for a refresh. */
    loadMore: () => void;
    /** The stored conversation, or null when it cannot be loaded. */
    open: (id: string) => Promise<ConversationDetail | null>;
}

export function useConversationHistory(
    fetchImpl: typeof fetch = fetch
): ConversationHistory {
    const rootStore = useStore();
    const [loaded, setLoaded] = useState<LoadedPages | null>(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasError, setHasError] = useState(false);
    const generation = useRef(0);
    const pageInFlight = useRef(false);
    // Read through a ref so refresh and open keep one identity for effects.
    const source = useRef({ fetchImpl, rootStore });
    source.current = { fetchImpl, rootStore };

    const signOutOnUnauthorized = useCallback((error: unknown) => {
        if (error instanceof AgentUnauthorizedError) {
            source.current.rootStore.handleUnauthorized();
        }
    }, []);

    const hasMore = loaded
        ? loaded.page * loaded.perPage < loaded.total
        : false;

    const refresh = useCallback(async () => {
        generation.current += 1;
        const mine = generation.current;
        setHasError(false);
        try {
            const first = await fetchConversationPage(
                source.current.fetchImpl,
                source.current.rootStore.getHeaders(),
                1
            );
            if (mine === generation.current) {
                setLoaded({
                    generation: mine,
                    conversations: first.results,
                    page: first.page,
                    perPage: first.perPage,
                    total: first.total,
                });
            }
        } catch (error) {
            signOutOnUnauthorized(error);
            if (mine === generation.current) {
                setHasError(true);
            }
        }
    }, [signOutOnUnauthorized]);

    const loadMore = useCallback(async () => {
        // A refresh in flight owns the next page: `loaded` still holds the pages it replaces.
        if (
            !loaded ||
            loaded.generation !== generation.current ||
            !hasMore ||
            hasError ||
            pageInFlight.current
        ) {
            return;
        }
        const mine = loaded.generation;
        pageInFlight.current = true;
        setIsLoadingMore(true);
        try {
            const next = await fetchConversationPage(
                source.current.fetchImpl,
                source.current.rootStore.getHeaders(),
                loaded.page + 1
            );
            if (mine === generation.current) {
                setLoaded((prev) => {
                    if (!prev || prev.generation !== mine) {
                        return prev;
                    }
                    const known = new Set(
                        prev.conversations.map(({ id }) => id)
                    );
                    return {
                        ...prev,
                        conversations: [
                            ...prev.conversations,
                            ...next.results.filter(({ id }) => !known.has(id)),
                        ],
                        page: next.page,
                        total: next.total,
                    };
                });
            }
        } catch (error) {
            signOutOnUnauthorized(error);
            if (mine === generation.current) {
                setHasError(true);
            }
        } finally {
            pageInFlight.current = false;
            setIsLoadingMore(false);
        }
    }, [hasError, hasMore, loaded, signOutOnUnauthorized]);

    const open = useCallback(
        async (id: string) => {
            try {
                return await fetchConversationDetail(
                    source.current.fetchImpl,
                    source.current.rootStore.getHeaders(),
                    id
                );
            } catch (error) {
                signOutOnUnauthorized(error);
                return null;
            }
        },
        [signOutOnUnauthorized]
    );

    return {
        conversations: loaded?.conversations ?? [],
        total: loaded?.total ?? 0,
        hasMore,
        isLoadingMore,
        hasError,
        refresh,
        loadMore,
        open,
    };
}
